import { useFormikContext } from 'formik'
import { electronicFormatIBAN } from 'ibantools'
import React, { useCallback, useMemo, useState } from 'react'

import { PAYMENT_METHODS } from '@constants/paymentMethods'
import { PAYMENT_PROVIDERS } from '@constants/paymentProviders'
import { PaymentMethodStatus } from '@enums'
import { useTranslation as useTranslationLib } from '@lib/i18n'
import { validateBIC, validateBICConsistency } from '@lib/validators/bic'
import { validateIBAN, isBICRequired } from '@lib/validators/iban'
import { SepaFormData } from '@lib/validators/sepa'
import { PaymentMethod } from '@pages/Checkout/hooks/Payment/useBookingPayment'
import PaymentLabel from '@pages/Checkout/Payment/Label'
import { SubscriptionFormData } from '@pages/Subscription/hooks/useInitialFormValues'
import { SubscriptionSEPAForm } from '@pages/Subscription/SEPAForm'

const useSepa = (): PaymentMethod => {
  const { t } = useTranslationLib()
  const { values, setFieldValue } = useFormikContext<SubscriptionFormData>()
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const handleSepaDataChange = useCallback(
    (sepaData: SepaFormData) => {
      setFieldValue('sepaData', sepaData)
    },
    [setFieldValue],
  )

  const validate = useCallback(() => {
    const errors: Record<string, any> = {}

    if (!values.sepaData) {
      errors.sepaData = t('subscription.sepa.dataRequired')
      return errors
    }

    const { iban, bic, holderName } = values.sepaData
    const sepaErrors: Record<string, string> = {}

    if (!iban || !iban.trim()) {
      sepaErrors.iban = t('subscription.sepa.ibanRequired')
    } else {
      const cleanIBAN = electronicFormatIBAN(iban)
      const ibanError = cleanIBAN ? validateIBAN(cleanIBAN) : t('subscription.sepa.ibanInvalid')
      if (ibanError) {
        sepaErrors.iban = ibanError
      }
    }

    const cleanIBAN = electronicFormatIBAN(iban)
    if (cleanIBAN && isBICRequired(cleanIBAN)) {
      if (!bic || !bic.trim()) {
        sepaErrors.bic = t('subscription.sepa.bicRequired')
      } else {
        const bicError = validateBIC(bic)
        if (bicError) {
          sepaErrors.bic = bicError
        } else {
          const consistencyError = validateBICConsistency(bic, cleanIBAN)
          if (consistencyError) {
            sepaErrors.bic = consistencyError
          }
        }
      }
    }

    if (!holderName || !holderName.trim()) {
      sepaErrors.holderName = t('subscription.sepa.accountHolderRequired')
    }

    if (Object.keys(sepaErrors).length > 0) {
      errors.sepaData = sepaErrors
    }

    return errors
  }, [values.sepaData, t])

  const sepaForm = useMemo(() => {
    const currentData = values.sepaData || {
      iban: '',
      bic: '',
      holderName: '',
    }

    return <SubscriptionSEPAForm data={currentData} onChange={handleSepaDataChange} errors={validationErrors} />
  }, [values.sepaData, handleSepaDataChange, validationErrors])

  const getOption = useCallback(
    () => ({
      value: PAYMENT_METHODS.SEPA_DIRECT_DEBIT,
      label: <PaymentLabel type={PAYMENT_METHODS.SEPA_DIRECT_DEBIT} showName={false} />,
      content: sepaForm,
    }),
    [sepaForm],
  )

  const submitForm = useCallback(async () => {
    if (!values.sepaData) {
      throw new Error('SEPA data is required')
    }

    const { iban, bic, holderName } = values.sepaData
    const cleanIBAN = electronicFormatIBAN(iban)

    if (!cleanIBAN) {
      throw new Error('Invalid IBAN')
    }

    const paymentMethodData: Record<string, unknown> = {
      iban: cleanIBAN,
      holderName,
      type: PAYMENT_METHODS.SEPA_DIRECT_DEBIT,
    }

    if (bic && bic.trim()) {
      paymentMethodData.bic = bic.trim()
    }

    return {
      paymentMethod: PAYMENT_METHODS.SEPA_DIRECT_DEBIT,
      paymentMethodData,
    }
  }, [values.sepaData])

  const beforeSubmit = useCallback(() => {
    const errors = validate()
    setValidationErrors(errors)
  }, [validate])

  return useMemo(
    () => ({
      status: PaymentMethodStatus.Ready,
      provider: PAYMENT_PROVIDERS.TRANSDEV,
      getOption,
      extraContent: sepaForm,
      on: {
        validate,
        submitForm,
        beforeSubmit,
      },
    }),
    [getOption, sepaForm, validate, submitForm, beforeSubmit],
  )
}

export default useSepa
